import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHtml5, FaCss3Alt, FaJs, FaReact, FaPython, FaDatabase, FaGitAlt, FaChartBar, FaTable, FaChartLine, FaGithub } from 'react-icons/fa';
import { SiTailwindcss, SiPandas, SiNumpy } from 'react-icons/si';

const SkillsContainer = styled.div`
  padding: 4rem 2rem;
  width: 100%;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled(motion.h2)`
  font-size: 1.8rem;
  font-family: 'Playfair Display', serif;
  color: #333;
  margin-bottom: 1.2rem;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    padding: 0;
  }
`;

const SkillCard = styled(motion.div)`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1.2rem;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.color};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 8px 25px rgba(13, 71, 161, 0.3);

    &:before {
      transform: scaleX(1);
    }
  }
`;

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.8rem;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.color};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.8rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    margin-right: 0.6rem;
  }

  svg {
    font-size: 1.4rem;
    color: white;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }

  ${SkillCard}:hover & {
    transform: rotate(10deg) scale(1.1);
  }
`;

const SkillTitle = styled.h3`
  font-size: 1.1rem;
  color: #333;
  font-family: 'Playfair Display', serif;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SkillDescription = styled.p`
  color: #666;
  font-size: 0.85rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

const Skills = () => {
  const skills = [
    {
      icon: <FaHtml5 />,
      title: "HTML5",
      description: "Semantic markup, accessibility, and modern HTML5 features",
      color: "#E44D26"
    },
    {
      icon: <FaCss3Alt />,
      title: "CSS3",
      description: "Responsive design, animations, and modern CSS features",
      color: "#264DE4"
    },
    {
      icon: <FaJs />,
      title: "JavaScript",
      description: "ES6+, DOM manipulation, and modern JavaScript features",
      color: "#F7DF1E"
    },
    {
      icon: <FaReact />,
      title: "React",
      description: "Component-based architecture, hooks, and state management",
      color: "#61DAFB"
    },
    {
      icon: <SiTailwindcss />,
      title: "Tailwind CSS",
      description: "Utility-first CSS framework for rapid UI development",
      color: "#38B2AC"
    },
    {
      icon: <FaPython />,
      title: "Python",
      description: "Data analysis, automation, and backend development",
      color: "#3776AB"
    },
    {
      icon: <SiPandas />,
      title: "Pandas",
      description: "Data manipulation and analysis with Python's powerful library",
      color: "#150458"
    },
    {
      icon: <SiNumpy />,
      title: "NumPy",
      description: "Scientific computing and numerical operations",
      color: "#013243"
    },
    {
      icon: <FaDatabase />,
      title: "SQL",
      description: "Database design, queries, and data management",
      color: "#336791"
    },
    {
      icon: <FaChartBar />,
      title: "Data Analysis",
      description: "Statistical analysis and data-driven insights",
      color: "#FF6B6B"
    },
    {
      icon: <FaChartLine />,
      title: "Data Visualization",
      description: "Creating insightful visual representations of data",
      color: "#4CAF50"
    },
    {
      icon: <FaGithub />,
      title: "GitHub",
      description: "Version control, collaboration, and project management",
      color: "#333333"
    }
  ];

  return (
    <SkillsContainer>
      <Content>
        <Header>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            MY SKILLS
          </Title>
        </Header>

        <SkillsGrid>
          {skills.map((skill, index) => (
            <SkillCard
              key={index}
              color={skill.color}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <SkillHeader>
                <IconWrapper color={skill.color}>
                  {skill.icon}
                </IconWrapper>
                <SkillTitle>{skill.title}</SkillTitle>
              </SkillHeader>
              <SkillDescription>{skill.description}</SkillDescription>
            </SkillCard>
          ))}
        </SkillsGrid>
      </Content>
    </SkillsContainer>
  );
};

export default Skills; 